import type { Moment } from 'moment-timezone';
import moment from 'moment-timezone';

export enum TimeFormat {
  // Example: 01/01/2020
  shortDate = 'L',
  // Example: Jan 1, 2020
  longDate = 'll',
  // Example: 01/01/2020, 5:00 PM
  shortDateTime = 'L, LT',
  // Example: 01/01/2020, 5:00 PM PST
  shortDateTimeZone = 'L, LT z',
  // Example: Jan 1, 2020 5:00 PM
  longDateTime = 'lll',
  // Example: January 2020
  monthYear = 'MMMM YYYY',
  // Example: 2020-01-01
  isoDate = 'YYYY-MM-DD',
  // Example: 2020-01-01T17:00:00.000
  isoDateTime = 'YYYY-MM-DDTHH:mm:ss.SSS',
  // Example: 2020-01-01 17:00:00
  intlDateTime = 'YYYY-MM-DD HH:mm:ss',
  // Example: January 1, 2020
  pickerDate = 'MMMM D, yyyy',
  // Example: January 1, 2020 5:00 PM
  pickerDateTime = 'MMMM D, yyyy h:mm a',
  // Example: January 1, 2020 5:00 PM PST
  pickerDateTimeZone = 'MMMM D, yyyy h:mm a z',
  // Example: January 1, 2020 5:00 PM PST
  pickerShortDateTimeZone = 'MMM D, yyyy h:mm a z',
  // Dynamic, shows relative time (e.g., "5 minutes ago", "in 2 hours")
  timeFromNow = 'TIME_FROM_NOW',
  // Dynamic, shows relative time with shortened units (e.g., "1s", "2d")
  // Only supports seconds, minutes, hours, and days.
  shortTimeFromNow = 'SHORT_TIME_FROM_NOW',
  // Example: Wednesday, January 1, 2020
  longWeekdayDate = 'dddd, MMMM D, YYYY',
  // Example: Wednesday, January 1, 2020 5:00 PM
  longWeekdayDateTime = 'dddd, MMMM D, YYYY LT',
  // Example: Wed, Jan 1, 2020
  shortWeekdayDate = 'ddd MMM D, YYYY z',
  // Example: Wed, Jan 1, 2020 5:00 PM PST
  shortWeekdayDateTime = 'ddd MMM D, YYYY LT z',
  // 5:00 PM PST
  timeAndTimeZone = 'LT z',
  // Example: Jan 1
  monthDay = 'MMM D',
  // Example: April 21, 2025
  fullDate = 'MMMM D, YYYY',
}

/**
 * Formats a date string into human-readable format
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Formatted date string like "April 21, 2025"
 */
export function humanDate(dateString: string): string {
  return moment.utc(dateString, TimeFormat.isoDate).format(TimeFormat.fullDate);
}

/**
 * Formats a date string into short human-readable format
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Formatted date string like "Jan 15"
 */
export function shortHumanDate(dateString: string): string {
  return moment.utc(dateString, TimeFormat.isoDate).format(TimeFormat.monthDay);
}

/**
 * Formats a date string into short date format with year
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Formatted date string like "Aug 1, 2025"
 */
export function formatShortDate(dateString: string): string {
  return moment.utc(dateString, TimeFormat.isoDate).format('MMM D, YYYY');
}

/**
 * Formats a date string into human-readable month format
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Formatted month string like "January 2025"
 */
export function humanMonth(dateString: string): string {
  return moment.utc(dateString, TimeFormat.isoDate).format(TimeFormat.monthYear);
}

export function localTime(
  timestamp: string = moment.utc().format(TimeFormat.isoDateTime),
  format: TimeFormat = TimeFormat.isoDateTime,
  inputFormat?: moment.MomentFormatSpecification,
): string {
  return moment.utc(timestamp, inputFormat).local().format(format);
}

/**
 * Get timezone abbreviation for date
 *
 * @param date
 * @param timezone
 * @returns
 */
export function timezoneAbbr(date?: Date, timezone: string = moment.tz.guess()): string {
  return moment(date).tz(timezone).zoneAbbr();
}

export function momentToDate(toConvert: Moment): Date {
  const asDate = toConvert.toDate();
  return new Date(
    Date.UTC(
      asDate.getUTCFullYear(),
      asDate.getUTCMonth(),
      asDate.getUTCDate(),
      asDate.getUTCHours(),
      asDate.getUTCMinutes(),
    ),
  );
}

/**
 * Advances a YYYY-MM-DD date string by one day, used to make an upper-bound filter inclusive of the end date.
 * Returns the input unchanged if it is not a plain ISO date string or if it is the max representable date.
 *
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Date string incremented by one day, e.g. "2026-04-08" → "2026-04-09"
 */
export function addDayToDate(dateString: string): string {
  const isoDatePattern = /^\d{4}-\d{2}-\d{2}$/;

  if (!isoDatePattern.test(dateString)) {
    return dateString;
  }

  const date = moment.utc(dateString, TimeFormat.isoDate);
  const maxDate = moment.utc('9999-12-31', TimeFormat.isoDate);

  if (date.isSameOrAfter(maxDate)) {
    return dateString;
  }

  return date.add(1, 'day').format(TimeFormat.isoDate);
}

export type TTimeUnit = 'days' | 'weeks' | 'months' | 'years';

/**
 * Returns a date string representing a past date based on the current date and the specified unit and amount.
 *
 * @param {Object} params - The parameters for calculating the past date.
 * @param {TUnit} params.unit - The unit of time to subtract (days, weeks, months, or years).
 * @param {number} params.amount - The amount of the specified unit to subtract.
 * @returns {string} - The calculated past date in ISO format (YYYY-MM-DD).
 */
export function getPastDate({ unit, amount }: { unit: TTimeUnit; amount: number }): string {
  const date = new Date();
  if (unit === 'days') {
    date.setDate(date.getDate() - amount);
  } else if (unit === 'weeks') {
    date.setDate(date.getDate() - amount * 7);
  } else if (unit === 'months') {
    date.setMonth(date.getMonth() - amount);
  } else if (unit === 'years') {
    date.setFullYear(date.getFullYear() - amount);
  }
  return date.toISOString().split('T')[0];
}

export default moment;

/*
 *const timestamp = '2021-02-22T16:05:24';
 *
 * // Now in Local time.
 *moment().format();
 *
 * // Now in UTC.
 *moment.utc().format();
 *
 * // Local time to UTC.
 *moment(timestamp).utc().format();
 *
 * // Local time.
 *moment(timestamp).format();
 *moment(timestamp).local().format();
 *
 * // UTC.
 *moment.utc(timestamp).format();
 *
 * // UTC to Local time.
 *moment.utc(timestamp).local().format();
 */
